/** The note, category and severity fields (spec rows 16, 17a and 17b). */
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  CATEGORY_OPTIONS,
  MAX_NOTE,
  NOTE_COUNTER_FROM,
  SEVERITY_OPTIONS,
  type FeedbackCategory,
  type FeedbackSeverity,
} from './constants';

interface Props {
  note: string;
  category: FeedbackCategory;
  severity: FeedbackSeverity;
  noteError: boolean;
  readOnly: boolean;
  onNote: (note: string) => void;
  onCategory: (category: FeedbackCategory) => void;
  onSeverity: (severity: FeedbackSeverity) => void;
}

export function NoteAndFields({
  note,
  category,
  severity,
  noteError,
  readOnly,
  onNote,
  onCategory,
  onSeverity,
}: Readonly<Props>) {
  const describedBy = [note.length >= NOTE_COUNTER_FROM ? 'feedback-note-count' : null, noteError ? 'feedback-note-error' : null]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="feedback-note">What happened?</Label>
        <Textarea
          id="feedback-note"
          rows={4}
          maxLength={MAX_NOTE}
          value={note}
          readOnly={readOnly}
          aria-required="true"
          aria-invalid={noteError || undefined}
          aria-describedby={describedBy || undefined}
          placeholder="Describe what's wrong, what you expected, and how to reproduce it."
          data-testid="feedback-note"
          onChange={(e) => onNote(e.target.value)}
        />
        {note.length >= NOTE_COUNTER_FROM && (
          <span id="feedback-note-count" className="text-xs text-muted-foreground">
            {note.length} / 10,000
          </span>
        )}
        {noteError && (
          <span id="feedback-note-error" data-testid="feedback-note-error" className="text-xs text-destructive-text">
            Add a note before sending.
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label id="feedback-category-label">Category</Label>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          spacing={0}
          value={category}
          disabled={readOnly}
          aria-label="Category"
          className="flex-wrap"
          data-testid="feedback-category"
          onValueChange={(value) => {
            // Re-clicking the pressed item reports an empty value; a category must always stay selected.
            const next = CATEGORY_OPTIONS.find((o) => o.value === value);
            if (next) onCategory(next.value);
          }}
        >
          {CATEGORY_OPTIONS.map((o) => (
            <ToggleGroupItem key={o.value} value={o.value} data-testid={`feedback-category-${o.value}`}>
              {o.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="feedback-severity">Severity</Label>
        <Select
          value={severity}
          disabled={readOnly}
          onValueChange={(value) => {
            const next = SEVERITY_OPTIONS.find((o) => o.value === value);
            if (next) onSeverity(next.value);
          }}
        >
          <SelectTrigger id="feedback-severity" size="sm" data-testid="feedback-severity">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SEVERITY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}
